/**
 * Product — /product
 *
 * Housing for everything stripped from the Fortune-500-clean landing page:
 *  · Who uses WaveScorePro (Stations · Agencies · Brands · Planners)
 *  · Confidence/source labelling explainer
 *  · Pricing preview (live, mirrors /billing)
 *  · Panel-vs-WaveScorePro comparison
 *  · Ghana focus note
 *
 * Public — no auth. Linked from the landing subscribe band and the footer.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { NavBar } from "@/components/NavBar";
import { WaveMark } from "@/components/WaveMark";
import { Button } from "@/design";
import { T } from "@/lib/theme";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const CUSTOMERS = [
  { icon: "📻", title: "Radio & TV Stations", color: T.green,
    items: [
      "Live listener count — people, not sets",
      "Minute-by-minute programme performance",
      "Full gender × age × LSM breakdown",
      "All 16 Ghana regions",
      "Independent Transmission Certificates",
    ] },
  { icon: "📊", title: "Advertising Agencies", color: T.blue,
    items: [
      "Airtime Value Engine — best slot for any brief",
      "Gender × Age × LSM planning",
      "CPTTA: Cost Per Thousand Target Audience",
      "Post-campaign broadcast evidence (fingerprint or stream-metadata, clearly labelled)",
      "Bulk certificate export",
    ] },
  { icon: "🏢", title: "Blue-Chip Brands", color: "#7C3AED",
    items: [
      "Target LSM 7-10 female 25-44 in Ashanti",
      "Independent ad verification",
      "Coverage vs industry giants for 5% of the cost",
      "Campaign reach reports with demographics",
      "Replaces expensive quarterly panel studies",
    ] },
  { icon: "🗺️", title: "Media Planners", color: "#7C3AED",
    cta: { label: "Try the Planning Tool →", href: "/plan" },
    items: [
      "Airtime Value Heatmap — see best slots at a glance",
      "CPTTA: Cost Per Thousand Target Audience",
      "Brief builder: gender × age × LSM × region × budget",
      "Budget simulator: how many spots for GH₵ X?",
      "Compare any station and slot side by side",
    ] },
];

const CONFIDENCE = [
  { badge: "DIRECT", color: "#059669", desc: "Stream server read", conf: "95%+" },
  { badge: "PANEL",  color: "#D97706", desc: "USSD panel responses", conf: "75-85%" },
  { badge: "MODEL",  color: "#2563EB", desc: "GSS statistical estimate", conf: "60-75%" },
  { badge: "HYBRID", color: "#D97706", desc: "Panel + model combined", conf: "70-80%" },
];

const COMPARISON = [
  ["Data refresh cadence", "Quarterly diary", "Live (60-second crawl + WebSocket push)"],
  ["Methodology", "Recall-based panel only", "DIRECT stream probes + PANEL + MODEL hybrid"],
  ["Independent ad verification", "Not offered", "Tamper-proof certificates with public verify URL"],
  ["Demographic granularity", "Region × age × gender", "Region × age × gender × LSM 1-2 → 9-10"],
  ["Time to a campaign plan", "2–6 weeks", "Under 5 minutes (Airtime Value Engine)"],
  ["Entry price (per year)", "USD 8,000 – 25,000+ subscription", "From USD 0 · Pro from USD 588/yr"],
  ["Station-side dashboard", "Not included", "Included — claim your station free"],
];

export default function Product() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [annualSavings, setAnnualSavings] = useState(17);
  const [audience, setAudience] = useState("station");
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    axios.get(`${API_BASE}/billing/plans`).then((r) => {
      setPlans(r.data.plans || []);
      setAnnualSavings(r.data.annual_savings_pct || 17);
    }).catch(() => { /* silent — pricing preview only, /billing is source of truth */ });
  }, []);

  const visiblePlans = plans.filter((p) => p.track === audience);

  return (
    <div data-testid="product-page" style={{
      fontFamily: "'Inter',system-ui,sans-serif",
      background: "#fff", color: T.navy, minHeight: "100vh",
    }}>
      <style>{`*{box-sizing:border-box}body{margin:0}`}</style>
      <NavBar dark={false} />

      {/* Page hero */}
      <section style={{
        background: "linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)",
        borderBottom: `1px solid ${T.border}`,
        padding: "64px 24px 48px",
      }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.green,
            letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 12 }}>
            The product
          </div>
          <h1 style={{ fontSize: "clamp(36px,4.4vw,58px)", fontWeight: 700,
            letterSpacing: "-0.02em", margin: "0 0 14px", color: T.navy }}>
            One platform. Four customers. Every number labelled.
          </h1>
          <p style={{ margin: "0 auto", maxWidth: 720, fontSize: 17,
            lineHeight: 1.6, color: T.muted }}>
            WaveScorePro serves broadcasters, agencies, brands and planners with real-time
            audience data — anchored on Ghana, expanding across West Africa.
          </p>
          <div data-testid="product-phase2-banner" style={{
            marginTop: 22, maxWidth: 720, marginLeft: "auto", marginRight: "auto",
            padding: "10px 14px", borderRadius: 10,
            background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.32)",
            color: "#92400E", fontSize: 12, lineHeight: 1.55,
          }}>
            <strong>Phase 1 scope:</strong> live digital connection counts and
            ad-verification certificates are available today. Demographic, LSM,
            and representative-reach features are marked <strong>Phase 2</strong>
            and arrive with our representative sampling layer.
          </div>
        </div>
      </section>

      {/* WHO USES IT */}
      <section data-testid="product-customers" style={{
        maxWidth: 1200, margin: "0 auto", padding: "72px 24px",
      }}>
        <div style={{ display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
          {CUSTOMERS.map((card, i) => (
            <div key={i} data-testid={`product-customer-${i}`}
              style={{ background: "#F9FAFB", borderRadius: 14,
                padding: "28px 24px", border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 34, marginBottom: 12 }}>{card.icon}</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: T.navy, margin: "0 0 14px" }}>
                {card.title}
              </h3>
              {card.items.map((pt, j) => (
                <div key={j} style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <span style={{ color: card.color, fontWeight: 700, flexShrink: 0 }}>✓</span>
                  <span style={{ color: T.muted, fontSize: 14 }}>{pt}</span>
                </div>
              ))}
              {card.cta && (
                <a href={card.cta.href}
                  style={{ display: "inline-block", marginTop: 12, color: card.color,
                    fontWeight: 700, fontSize: 13, textDecoration: "none",
                    borderBottom: `1px solid ${card.color}55`, paddingBottom: 1 }}>
                  {card.cta.label}
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CONFIDENCE */}
      <section data-testid="product-confidence" style={{
        background: "#F8FAFC", borderTop: `1px solid ${T.border}`,
        borderBottom: `1px solid ${T.border}`, padding: "72px 24px",
      }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: "clamp(24px,3vw,32px)", fontWeight: 700,
            letterSpacing: "-0.02em", color: T.navy, margin: "0 0 12px" }}>
            Every number tells you how to trust it
          </h2>
          <p style={{ color: T.muted, fontSize: 16, margin: "0 auto 36px", maxWidth: 640 }}>
            Every data point is labelled with its source and confidence level.
            No hidden estimates. Full transparency.
          </p>
          <div style={{ display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
            {CONFIDENCE.map((item) => (
              <div key={item.badge} data-testid={`product-confidence-${item.badge}`}
                style={{ background: "#fff", borderRadius: 12,
                  padding: "22px 18px", border: `1px solid ${T.border}` }}>
                <div style={{ background: item.color, color: "#fff",
                  padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 800,
                  fontFamily: "monospace", display: "inline-block", marginBottom: 12 }}>
                  {item.badge}
                </div>
                <div style={{ color: T.navy, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  {item.desc}
                </div>
                <div style={{ color: item.color, fontSize: 20, fontWeight: 800 }}>{item.conf}</div>
                <div style={{ color: T.muted, fontSize: 11 }}>confidence</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section data-testid="product-pricing" style={{
        maxWidth: 1180, margin: "0 auto", padding: "80px 24px",
      }}>
        <h2 style={{ fontSize: "clamp(24px,3vw,32px)", fontWeight: 700,
          letterSpacing: "-0.02em", color: T.navy, textAlign: "center", margin: "0 0 8px" }}>
          Pricing built for Ghana
        </h2>
        <p style={{ color: T.muted, textAlign: "center", margin: "0 auto 24px",
          maxWidth: 620, lineHeight: 1.6 }}>
          One platform, two audiences. Billed in Ghanaian Cedis (GH₵) via Paystack.
          USD equivalents shown for reference.{" "}
          <b style={{ color: T.green }}>Annual saves {annualSavings}%</b>.
        </p>

        <div style={{ display: "flex", justifyContent: "center", gap: 12,
          marginBottom: 36, flexWrap: "wrap" }}>
          <div data-testid="product-audience-toggle" style={{ display: "inline-flex",
            background: "#F1F5F9", border: `1px solid ${T.border}`,
            borderRadius: 10, padding: 4 }}>
            {[["station", "📻 Stations"], ["agency", "🎯 Agencies & Brands"]].map(([k, label]) => (
              <button key={k} data-testid={`product-audience-${k}`} onClick={() => setAudience(k)}
                style={{ padding: "8px 16px",
                  background: audience === k ? T.green : "transparent",
                  color: audience === k ? "#fff" : T.muted, border: "none",
                  borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700,
                  fontFamily: "inherit" }}>{label}</button>
            ))}
          </div>
          <div data-testid="product-cycle-toggle" style={{ display: "inline-flex",
            background: "#F1F5F9", border: `1px solid ${T.border}`,
            borderRadius: 10, padding: 4 }}>
            {[["monthly", "Monthly"], ["annual", `Annual · save ${annualSavings}%`]].map(([k, label]) => (
              <button key={k} data-testid={`product-cycle-${k}`} onClick={() => setCycle(k)}
                style={{ padding: "8px 16px",
                  background: cycle === k ? T.navy : "transparent",
                  color: cycle === k ? "#fff" : T.muted, border: "none",
                  borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700,
                  fontFamily: "inherit" }}>{label}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          {visiblePlans.map((p) => {
            const usd = cycle === "annual" ? p.annual_usd : p.monthly_usd;
            const ghs = cycle === "annual" ? p.annual_ghs : p.monthly_ghs;
            const isFree = p.monthly_usd === 0;
            const popular = p.popular;
            return (
              <div key={p.id} data-testid={`product-plan-${p.id}`}
                style={{ borderRadius: 14, padding: "28px 24px", position: "relative",
                  background: popular ? T.navy : "#fff",
                  border: popular ? "none" : `1px solid ${T.border}`,
                  boxShadow: popular ? "0 20px 60px rgba(10,22,40,0.18)" : "none" }}>
                {popular && (
                  <div style={{ position: "absolute", top: -12, left: "50%",
                    transform: "translateX(-50%)",
                    background: T.green, color: "#fff", padding: "4px 16px",
                    borderRadius: 20, fontSize: 11, fontWeight: 800,
                    letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
                    MOST POPULAR
                  </div>
                )}
                <div style={{ fontSize: 11, fontWeight: 700,
                  color: popular ? "#94A3B8" : T.muted,
                  letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
                  {p.track === "agency" ? "AGENCY" : "STATION"}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800,
                  color: popular ? "#fff" : T.navy, marginBottom: 6 }}>
                  {p.name}
                </div>
                <p style={{ fontSize: 12, color: popular ? "#CBD5E1" : T.muted,
                  margin: "0 0 14px", minHeight: 30, lineHeight: 1.45 }}>
                  {p.tagline}
                </p>
                {isFree ? (
                  <div style={{ fontSize: 32, fontWeight: 800, color: T.green,
                    marginBottom: 18 }}>Free</div>
                ) : (
                  <div style={{ marginBottom: 18 }}>
                    <div data-testid={`product-price-${p.id}`}
                      style={{ fontSize: 32, fontWeight: 800,
                        color: popular ? "#fff" : T.navy, lineHeight: 1 }}>
                      GH₵ {Number(ghs).toLocaleString()}
                      <span style={{ color: popular ? "#94A3B8" : T.muted,
                        fontSize: 14, fontWeight: 500 }}>
                        {cycle === "annual" ? "/yr" : "/mo"}
                      </span>
                    </div>
                    <div style={{ fontSize: 11,
                      color: popular ? "#94A3B8" : T.muted, marginTop: 2 }}>
                      ≈ ${usd} {cycle === "annual" ? "/yr" : "/mo"}
                    </div>
                  </div>
                )}
                {(p.features || []).map((f, j) => {
                  // Some plan features are strings, others are `{label, phase}`
                  // objects flagging Phase-2 upcoming capabilities. Render the
                  // label and dim the row when it's phase-gated.
                  const label = typeof f === "string" ? f : (f?.label || "");
                  const upcoming = typeof f !== "string" && f?.phase;
                  return (
                    <div key={j} style={{ display: "flex", gap: 10, marginBottom: 8, opacity: upcoming ? 0.6 : 1 }}>
                      <span style={{ color: T.green, fontWeight: 700, flexShrink: 0 }}>✓</span>
                      <span style={{ color: popular ? "#CBD5E1" : T.muted,
                        fontSize: 13, lineHeight: 1.5 }}>
                        {label}
                        {upcoming && (
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700,
                            color: T.muted, background: "#F1F5F9", padding: "1px 6px",
                            borderRadius: 4 }}>coming</span>
                        )}
                      </span>
                    </div>
                  );
                })}
                <button data-testid={`product-cta-${p.id}`}
                  onClick={() => nav(isFree ? "/register" : "/billing")}
                  style={{ display: "block", width: "100%", textAlign: "center",
                    marginTop: 18, padding: "11px", borderRadius: 8,
                    fontWeight: 700, fontSize: 13, cursor: "pointer",
                    background: popular ? T.green : "transparent",
                    color: popular ? "#fff" : T.green,
                    border: popular ? "none" : `1px solid ${T.green}`,
                    fontFamily: "inherit" }}>
                  {isFree ? "Get started" : `Choose ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>
        <p style={{ textAlign: "center", color: T.muted, fontSize: 13, marginTop: 28 }}>
          Need 10+ stations or a multinational rollout?{" "}
          <a href="mailto:sales@wavescorepro.com" style={{ color: T.green }}>Talk to Enterprise →</a>
        </p>
      </section>

      {/* COMPARISON */}
      <section data-testid="product-comparison" style={{
        padding: "72px 24px", background: "#F8FAFC",
        borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <h2 style={{ fontSize: "clamp(24px,3vw,32px)", fontWeight: 700,
            letterSpacing: "-0.02em", color: T.navy, textAlign: "center", margin: "0 0 8px" }}>
            How we compare
          </h2>
          <p style={{ color: T.muted, textAlign: "center", margin: "0 auto 32px",
            maxWidth: 620, lineHeight: 1.6 }}>
            Ghana&apos;s agencies have relied on quarterly diary studies for two decades.
            Here&apos;s how WaveScorePro changes the playbook.
          </p>
          <div style={{ overflowX: "auto", borderRadius: 14,
            border: `1px solid ${T.border}`, background: "#fff",
            boxShadow: "0 6px 24px rgba(10,22,40,0.06)" }}>
            <table data-testid="product-comparison-table" style={{
              width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 640 }}>
              <thead>
                <tr style={{ background: T.navy, color: "#fff" }}>
                  <th style={{ textAlign: "left", padding: "16px 20px",
                    fontWeight: 700, fontSize: 12, letterSpacing: "0.08em",
                    textTransform: "uppercase" }}>Capability</th>
                  <th style={{ textAlign: "left", padding: "16px 20px",
                    fontWeight: 700, fontSize: 12, letterSpacing: "0.08em",
                    textTransform: "uppercase", color: "#94A3B8" }}>
                    Industry-standard panel diaries
                  </th>
                  <th style={{ textAlign: "left", padding: "16px 20px",
                    fontWeight: 700, fontSize: 12, letterSpacing: "0.08em",
                    textTransform: "uppercase", color: T.green }}>WaveScorePro</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map(([cap, panel, ws], i) => (
                  <tr key={i} style={{ borderTop: i === 0 ? "none" : `1px solid ${T.border}` }}>
                    <td style={{ padding: "14px 20px", color: T.navy, fontWeight: 600 }}>{cap}</td>
                    <td style={{ padding: "14px 20px", color: T.muted }}>{panel}</td>
                    <td style={{ padding: "14px 20px", color: T.navy, fontWeight: 600 }}>
                      <span style={{ color: T.green, marginRight: 6, fontWeight: 800 }}>✓</span>{ws}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA back to landing */}
      <section style={{ padding: "72px 24px", textAlign: "center" }}>
        <h2 style={{ fontSize: "clamp(22px,3vw,28px)", fontWeight: 700, letterSpacing: "-0.02em",
          color: T.navy, margin: "0 0 14px" }}>
          Ready to start?
        </h2>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Button data-testid="product-cta-listen" size="lg" onClick={() => nav("/listen")}
            style={{ background: T.navy, borderColor: T.navy }}>
            Listen free →
          </Button>
          <Button data-testid="product-cta-register" size="lg" onClick={() => nav("/register")}
            style={{ background: T.green, borderColor: T.green }}>
            Get started free
          </Button>
        </div>
      </section>

      <footer style={{
        background: T.navy, color: "rgba(255,255,255,0.65)",
        padding: "36px 24px", textAlign: "center", fontSize: 13,
      }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10,
          color: "#fff", fontWeight: 800, fontSize: 17, marginBottom: 6 }}>
          <WaveMark size={24} inverted /> WaveScore<span style={{ color: T.green }}>Pro</span>
        </div>
        <div>© 2026 <b style={{ color: "rgba(255,255,255,0.85)" }}>I-Assist Pro Limited</b> ·
          {" "}Accra, Ghana ·{" "}
          <a href="tel:+233208553679" style={{ color: "rgba(255,255,255,0.85)", textDecoration: "none" }}>
            +233 20 855 3679
          </a>
        </div>
      </footer>
    </div>
  );
}
